import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NewsCard = ({ news }) => {
  const getCategoryColor = (category) => {
    const colors = {
      hr: 'var(--color-info)',
      payroll: 'var(--color-success)',
      tax: 'var(--color-warning)',
      minimum_wage: 'var(--color-error)',
      employment_act: 'var(--color-primary)',
      gazette: 'var(--color-accent)',
      general: 'var(--color-muted-foreground)'
    };
    return colors?.[category] || 'var(--color-muted-foreground)';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      hr: 'Users',
      payroll: 'DollarSign',
      tax: 'Receipt',
      minimum_wage: 'TrendingUp',
      employment_act: 'FileText',
      gazette: 'BookOpen',
      general: 'Info'
    };
    return icons?.[category] || 'Info';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'var(--color-error)',
      high: 'var(--color-warning)',
      medium: 'var(--color-info)',
      low: 'var(--color-success)'
    };
    return colors?.[priority] || 'var(--color-muted-foreground)';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      critical: 'AlertTriangle',
      high: 'AlertCircle',
      medium: 'Info',
      low: 'CheckCircle'
    };
    return icons?.[priority] || 'Info';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      hr: 'HR & Employment',
      payroll: 'Payroll',
      tax: 'Tax (SWT)',
      minimum_wage: 'Minimum Wage',
      employment_act: 'Employment Act',
      gazette: 'Gazette',
      general: 'General'
    };
    return labels?.[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 transition-smooth hover:shadow-md">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
              style={{ backgroundColor: `${getCategoryColor(news?.category)}15`, color: getCategoryColor(news?.category) }}
            >
              <Icon name={getCategoryIcon(news?.category)} size={12} color="currentColor" />
              {getCategoryLabel(news?.category)}
            </div>
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
              style={{ backgroundColor: `${getPriorityColor(news?.priority)}15`, color: getPriorityColor(news?.priority) }}
            >
              <Icon name={getPriorityIcon(news?.priority)} size={12} color="currentColor" />
              {news?.priority?.toUpperCase()}
            </div>
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
            {news?.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {news?.summary}
          </p>
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-4">
        <div className="flex items-center gap-1">
          <Icon name="Calendar" size={14} color="currentColor" />
          <span>Published: {formatDate(news?.publicationDate)}</span>
        </div>
        {news?.effectiveDate && (
          <div className="flex items-center gap-1">
            <Icon name="Clock" size={14} color="currentColor" />
            <span>Effective: {formatDate(news?.effectiveDate)}</span>
          </div>
        )}
        {news?.gazetteReference && (
          <div className="flex items-center gap-1">
            <Icon name="FileText" size={14} color="currentColor" />
            <span>{news?.gazetteReference}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Icon name="Building2" size={14} color="currentColor" />
          <span>{news?.source}</span>
        </div>
      </div>

      {/* Tags */}
      {news?.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {news?.tags?.slice(0, 4)?.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          iconName="Eye"
          iconPosition="left"
        >
          Read Full Article
        </Button>
        {news?.sourceUrl && (
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="left"
            onClick={() => window.open(news?.sourceUrl, '_blank')}
          >
            View Source
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewsCard;